import React, { useState, useRef, useEffect } from "react";
import { FaUser, FaSignOutAlt, FaChevronDown, FaShippingFast } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, User } from "lucide-react";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { clientData, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
    const {t, i18n} =useTranslation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center  
        w-10 h-10 rounded-full"
      >
        <User size={24} className="text-black" />
        <FaChevronDown className="ml-1 w-3 h-3 text-black" />
      </button>

      {isOpen && (
        <div
          className={`absolute  mt-2 w-56 bg-white divide-y divide-gray-100  rounded-lg shadow-lg   ring-1 ring-black ring-opacity-5 focus:outline-none z-50 
            ${i18n.dir() === "rtl"?'left-0  origin-top-left': 'right-0  origin-top-right'  }`}
        >
          {isAuthenticated && (
            <>
              <div className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {clientData?.username}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {clientData?.email}
                </p>
              </div>

              <div className="py-1">
                <Link
                     to={{
                      pathname: "/update",  // Path to the destination
                      state: { profile: clientData }  // Pass the profile data in the state
                    }}
                  className="flex items-center w-full p-3 
              text-sm text-gray-700 
              hover:bg-gray-100 
              focus:outline-none"
                >
                  <FaUser className="w-4 h-4 text-gray-500" />
                  
                  <span className="ms-3">{t('editProfile')}</span>
                </Link>
              </div>
              <div className="py-1">
                <Link
                     to={'/orders'}
                  className="flex items-center w-full p-3 
              text-sm text-gray-700 
              hover:bg-gray-100 
              focus:outline-none"
                >
                  <FaShippingFast className="w-4 h-4 text-gray-500" />
                  
                  <span className="ms-3">{t('MyOrders')}</span>
                </Link>
              </div>

              <div className="py-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full p-3 
              text-sm text-red-600 
              hover:bg-red-50 
              focus:outline-none"
                >
                  <FaSignOutAlt className=" w-4 h-4 text-red-500" />
                  <span className="ms-3">{t('logout')}</span>
                </button>
              </div>
            </>
          )}
        <div className="py-1">
        <button
        onClick={() => setIsLangModalOpen(true)}
              className="flex items-center p-2 hover:bg-gray-100  font-[roboto] w-full "
            >
              <svg
                className="flex-shrink-0 w-5 h-5"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_84_170)">
                  <path
                    d="M4.84688 27.6656L7.81875 28.2562C8.55937 28.4062 9.31875 28.1719 9.85312 27.6375L11.2969 26.1937C12.3844 25.1062 14.2125 25.4062 14.8969 26.775L15.7687 28.5094C16.2188 29.4094 17.1375 29.9812 18.15 29.9812C19.575 29.9812 20.5969 28.6125 20.1844 27.2438L19.6219 25.3781C19.1906 23.9344 20.2688 22.4813 21.7781 22.4813H21.9938C23.25 22.4813 24.4219 21.8531 25.1156 20.8125L26.1188 19.3031C26.6438 18.5062 26.6156 17.4656 26.0437 16.7062L24.5344 14.6906C23.5688 13.4062 24.225 11.55 25.7906 11.1562L27.3844 10.7531C28.0875 10.575 28.6594 10.0781 28.9313 9.40313L30.4688 5.56875C28.4438 4.88438 26.2687 4.5 24 4.5C13.2281 4.5 4.5 13.2281 4.5 24C4.5 25.2563 4.62188 26.4844 4.84688 27.6656ZM43.0687 28.0969C42.7875 28.0688 42.5062 28.0875 42.225 28.1719L40.7438 28.5844C40.1156 28.7625 39.45 28.5 39.1031 27.9563L38.9156 27.6656C38.3531 26.7844 37.3781 26.25 36.3281 26.25C35.2781 26.25 34.3031 26.7844 33.7406 27.6656L33.1687 28.5563C33.0375 28.7625 32.85 28.9406 32.6344 29.0531L29.25 30.9469C27.5531 31.8938 26.8594 33.9844 27.6562 35.7562L28.1719 36.9187C28.9781 38.7188 31.05 39.5906 32.9062 38.8969L33.15 38.8031C34.0875 38.4562 35.1469 38.5969 35.9531 39.1875L36.0938 39.2906C39.5812 36.525 42.1031 32.5969 43.0687 28.0875V28.0969ZM0 24C0 17.6348 2.52856 11.5303 7.02944 7.02944C11.5303 2.52856 17.6348 0 24 0C30.3652 0 36.4697 2.52856 40.9706 7.02944C45.4714 11.5303 48 17.6348 48 24C48 30.3652 45.4714 36.4697 40.9706 40.9706C36.4697 45.4714 30.3652 48 24 48C17.6348 48 11.5303 45.4714 7.02944 40.9706C2.52856 36.4697 0 30.3652 0 24ZM13.5469 32.6344C13.35 33.4406 13.8375 34.2562 14.6344 34.4531L17.6344 35.2031C18.4406 35.4 19.2563 34.9125 19.4531 34.1156C19.65 33.3187 19.1625 32.4938 18.3656 32.2969L15.3656 31.5469C14.5594 31.35 13.7437 31.8375 13.5469 32.6344ZM22.1719 30.7594C21.975 31.5656 22.4625 32.3812 23.2594 32.5781C24.0563 32.775 24.8813 32.2875 25.0781 31.4906L25.8281 28.4906C26.025 27.6844 25.5375 26.8687 24.7406 26.6719C23.9437 26.475 23.1187 26.9625 22.9219 27.7594L22.1719 30.7594ZM32.175 10.6594C31.4344 10.2844 30.5344 10.5938 30.1594 11.3344L28.6594 14.3344C28.2844 15.075 28.5938 15.975 29.3344 16.35C30.075 16.725 30.975 16.4156 31.35 15.675L32.85 12.675C33.225 11.9344 32.9156 11.0344 32.175 10.6594Z"
                    fill="#AAAAAA"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_84_170">
                    <rect width="48" height="48" fill="white" />
                  </clipPath>
                </defs>
              </svg>

              <span className="ms-3">{t('language')}</span>
            </button>
                <LanguageSelector
                  isOpen={isLangModalOpen}
                  onClose={() => setIsLangModalOpen(false)}
                />
              </div>
          {!isAuthenticated && (
            <div className="py-1">
              <Link
                to="/login"
                className="flex items-center space-x-2 w-full p-3 
      text-sm text-red-600 
      hover:bg-red-50 
      focus:outline-none"
              >
                <LogIn size={24} /> <span>{t('login')}</span>
              </Link>
            </div>
              
          )}
        </div>
      )}
    </div>
  );
};

export default Profile;
